import { styled } from '@linaria/react';
import { TextInput } from '@mantine/core';
import { useState } from 'react';
import type { Subproject } from '../../../../api/integrations-client/integrations-client.type';
import { primaryBase, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DataManagementDropShadowContainer } from '../data-management-header';
import type { BoardSetting } from './board-settings-client.type';
import { getProjectGroups, UNKNOWN_SUBPROJECT_NAME } from './board-settings.helpers';
import type { ExtendedSubproject } from './board-settings.type';

export type PendingSubprojectChanges = {
  toAdd: string[];
  toRemove: string[];
};

type EditBoardSettingsPanelProps = {
  selectedBoardSetting: BoardSetting;
  subprojects: ExtendedSubproject[];
  setIsEditing: (isEditing: boolean) => void;
  pendingSubprojectChanges: PendingSubprojectChanges;
  onSubprojectChange: (changes: PendingSubprojectChanges) => void;
  isSaving: boolean;
};

export function EditBoardSettingsPanel({
  selectedBoardSetting,
  subprojects,
  setIsEditing,
  pendingSubprojectChanges,
  onSubprojectChange,
  isSaving,
}: EditBoardSettingsPanelProps) {
  const [search, setSearch] = useState('');

  // Helper function to determine if a subproject is effectively selected
  const isSubprojectSelected = (subproject: Subproject): boolean => {
    const isCurrentlyMapped = subproject.subproject_settings_id === selectedBoardSetting.id;
    const isPendingAdd = pendingSubprojectChanges.toAdd.includes(subproject.id);
    const isPendingRemove = pendingSubprojectChanges.toRemove.includes(subproject.id);

    // If pending add, it's selected
    if (isPendingAdd) return true;
    // If pending remove, it's not selected
    if (isPendingRemove) return false;
    // Otherwise use the current mapped state
    return isCurrentlyMapped;
  };

  const handleBoardToggle = (subproject: Subproject) => {
    if (isSaving) return;

    const isCurrentlyMapped = subproject.subproject_settings_id === selectedBoardSetting.id;
    const isPendingAdd = pendingSubprojectChanges.toAdd.includes(subproject.id);
    const isPendingRemove = pendingSubprojectChanges.toRemove.includes(subproject.id);

    let newToAdd = [...pendingSubprojectChanges.toAdd];
    let newToRemove = [...pendingSubprojectChanges.toRemove];

    if (isCurrentlyMapped) {
      // Currently mapped to this board setting
      if (isPendingRemove) {
        // Already pending removal, toggle back (cancel removal)
        newToRemove = newToRemove.filter((id) => id !== subproject.id);
      } else {
        // Add to pending removal
        newToRemove.push(subproject.id);
      }
    } else {
      // Not currently mapped to this board setting
      if (isPendingAdd) {
        // Already pending add, toggle back (cancel add)
        newToAdd = newToAdd.filter((id) => id !== subproject.id);
      } else {
        // Add to pending additions
        newToAdd.push(subproject.id);
      }
    }

    onSubprojectChange({ toAdd: newToAdd, toRemove: newToRemove });
  };

  const searchTerm = search.toLowerCase().trim();
  const filteredSubprojects = subprojects.filter((subproject) => {
    const name = (subproject.name ?? '').toLowerCase();
    const projectName = (subproject.project_name ?? '').toLowerCase();
    return name.includes(searchTerm) || projectName.includes(searchTerm);
  });

  const projectGroups = getProjectGroups(filteredSubprojects);

  return (
    <EditBoardSettingsPanelContainer>
      <HeaderContainer>
        <Text weight="bold" size="large">
          Add Boards
        </Text>
        <Icon
          size={24}
          name="close"
          color={skyDark}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setIsEditing(false);
          }}
        />
      </HeaderContainer>
      <Divider style={{ marginTop: 30, marginBottom: 12 }} />
      <TextInput
        placeholder="Search for Project or Board"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        leftSection={<Icon name="search" size={16} color={skyDark} />}
        style={{ width: 250 }}
        radius="xl"
        size="xs"
      />
      <Divider style={{ marginTop: 12, marginBottom: 12 }} />
      <ProjectListContainer>
        {Object.entries(projectGroups).map(([projectId, { name: projectName, subprojects: projectSubprojects }]) => (
          <div key={projectId}>
            <Text weight="medium">{projectName}</Text>
            {projectSubprojects.map((subproject) => {
              const isSelected = isSubprojectSelected(subproject);
              const isSelectedOtherBoard =
                subproject.subproject_settings_id &&
                subproject.subproject_settings_id !== selectedBoardSetting.id &&
                !pendingSubprojectChanges.toAdd.includes(subproject.id);
              return (
                <BoardRowContainer key={subproject.id}>
                  <Text>{subproject.name ?? UNKNOWN_SUBPROJECT_NAME}</Text>
                  {isSelectedOtherBoard ? (
                    <Button
                      variant="outline"
                      size="xs"
                      radius="xl"
                      disabled={isSaving}
                      rightSection={<Icon name="close" size={16} color={skyDark} />}
                      onClick={() => handleBoardToggle(subproject)}
                    >
                      <Text size="tiny">{subproject.subproject_settings_name}</Text>
                    </Button>
                  ) : (
                    <Icon
                      name={isSelected ? 'check_circle' : 'add_circle_outline'}
                      size={20}
                      color={isSelected ? secondaryBase : primaryBase}
                      style={{
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        opacity: isSaving ? 0.5 : 1,
                        marginRight: 16,
                      }}
                      onClick={() => !isSaving && handleBoardToggle(subproject)}
                    />
                  )}
                </BoardRowContainer>
              );
            })}
          </div>
        ))}
      </ProjectListContainer>
    </EditBoardSettingsPanelContainer>
  );
}

const EditBoardSettingsPanelContainer = styled(DataManagementDropShadowContainer)`
  max-width: 285px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ProjectListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
`;

const BoardRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 8px;
`;
